import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  UserX,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Users,
  Save,
  X,
  Mail,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const emptyForm = { firstName: '', lastName: '', email: '', storeIdFk: '' };

// Sales reps and ASD contacts share one table and one screen - they differ only
// in who gets mailed: reps on every handoff, ASD only on an opted-in manual one.
const RECIPIENT_TYPES = {
  SALES_REP: {
    key: 'SALES_REP',
    title: 'Sales Reps',
    noun: 'sales representative',
    addLabel: 'Add Rep',
    editLabel: 'Edit Rep',
    blurb: 'Emailed on every handoff',
    countNoun: 'rep',
    chip: 'bg-blue-100 text-blue-800',
    emptyChip: 'bg-amber-100 text-amber-800',
    button: 'bg-blue-600 hover:bg-blue-700',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  ASD: {
    key: 'ASD',
    title: 'ASD',
    noun: 'ASD contact',
    addLabel: 'Add ASD',
    editLabel: 'Edit ASD',
    blurb: 'Directors and managers, only on an opted-in manual handoff',
    countNoun: 'ASD',
    chip: 'bg-purple-100 text-purple-800',
    emptyChip: 'bg-gray-100 text-gray-600',
    button: 'bg-purple-600 hover:bg-purple-700',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
};

const TYPE_LIST = Object.values(RECIPIENT_TYPES);

const typeOf = (rep) => rep.recipientType || RECIPIENT_TYPES.SALES_REP.key;

const SalesRepsManagement = () => {
  const [stores, setStores] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [reps, setReps] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState('');
  const [selectedBocId, setSelectedBocId] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  // Which list the open dialog belongs to - both lists are on screen at once,
  // so the type comes from the button that opened it, not from a page-level tab.
  const [dialogType, setDialogType] = useState(RECIPIENT_TYPES.SALES_REP.key);

  const dialogMeta = RECIPIENT_TYPES[dialogType];
  const { confirm, confirmDialog } = useConfirmDialog();

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [storesRes, repsRes, configsRes, enterprisesRes, bocsRes] = await Promise.all([
        api.get('/admin/stores'),
        api.get('/admin/sales-reps/all'),
        api.get('/admin/email-config/all'),
        api.get('/admin/enterprises'),
        api.get('/admin/bocs'),
      ]);
      setStores(storesRes.data);
      setReps(repsRes.data);
      setConfigs(configsRes.data);
      setEnterprises(enterprisesRes.data);
      setBocs(bocsRes.data);
    } catch (err) {
      console.error('Error loading sales reps overview:', err);
      setError('Failed to load stores and sales representatives');
    } finally {
      setLoading(false);
    }
  };

  const storeLabel = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    return store ? (store.name || store.storeId) : `Store #${storeId}`;
  };

  const repsFor = (storeId, type) =>
    reps.filter((r) => r.storeIdFk === storeId && (!type || typeOf(r) === type));
  const hasConfig = (storeId) => configs.some((c) => c.storeIdFk === storeId);

  const bocsForSelectedEnterprise = selectedEnterpriseId
    ? bocs.filter((b) => b.enterpriseIdFk === selectedEnterpriseId)
    : [];

  const filteredStores = stores.filter((s) => {
    if (selectedEnterpriseId && s.enterpriseIdFk !== selectedEnterpriseId) return false;
    if (selectedBocId && s.bocIdFk !== selectedBocId) return false;
    return true;
  });

  const handleEnterpriseChange = (value) => {
    setSelectedEnterpriseId(value);
    setSelectedBocId('');
    setSelectedStoreId(null);
  };

  const handleBocChange = (value) => {
    setSelectedBocId(value);
    setSelectedStoreId(null);
  };

  const summary = useMemo(() => {
    const total = stores.length;
    const covered = stores.filter(
      (s) => repsFor(s.id, RECIPIENT_TYPES.SALES_REP.key).length > 0
    ).length;
    const withAsd = stores.filter(
      (s) => repsFor(s.id, RECIPIENT_TYPES.ASD.key).length > 0
    ).length;
    return { total, covered, withAsd };
  }, [stores, reps]);

  const selectedReps = selectedStoreId != null ? repsFor(selectedStoreId) : [];

  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'First name is required';
    if (!formData.lastName.trim()) errs.lastName = 'Last name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Valid email is required';
    if (!formData.storeIdFk) errs.storeIdFk = 'Store is required';
    const duplicate = reps.some(
      (r) =>
        r.storeIdFk === Number(formData.storeIdFk) &&
        typeOf(r) === dialogType &&
        r.email.toLowerCase() === formData.email.trim().toLowerCase() &&
        (!editingRep || r.id !== editingRep.id)
    );
    if (duplicate) errs.email = `A ${dialogMeta.noun} with this email already exists for this store`;
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...formData,
        storeIdFk: Number(formData.storeIdFk),
        recipientType: editingRep ? typeOf(editingRep) : dialogType,
      };
      if (editingRep) {
        const res = await api.put(`/admin/sales-reps/${editingRep.id}`, payload);
        setReps((prev) => prev.map((r) => (r.id === editingRep.id ? res.data : r)));
      } else {
        const res = await api.post('/admin/sales-reps', payload);
        setReps((prev) => [...prev, res.data]);
      }
      setSuccess(editingRep ? `${dialogMeta.noun} updated` : `${dialogMeta.noun} added`);
      setTimeout(() => setSuccess(''), 2500);
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving sales representative:', err);
      setError(err.response?.data?.message || `Failed to save ${dialogMeta.noun}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (rep) => {
    setDialogType(typeOf(rep));
    setEditingRep(rep);
    setError('');
    setFormData({
      firstName: rep.firstName || '',
      lastName: rep.lastName || '',
      email: rep.email || '',
      storeIdFk: rep.storeIdFk != null ? String(rep.storeIdFk) : '',
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleAddNew = (type) => {
    setDialogType(type || RECIPIENT_TYPES.SALES_REP.key);
    setEditingRep(null);
    setError('');
    setFormData({ ...emptyForm, storeIdFk: selectedStoreId != null ? String(selectedStoreId) : '' });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleDeactivate = async (rep) => {
    const remaining = repsFor(rep.storeIdFk, typeOf(rep)).length - 1;
    const warning = remaining === 0
      ? typeOf(rep) === RECIPIENT_TYPES.ASD.key
        ? `\n\nWARNING: this is the last active ASD contact for ${storeLabel(rep.storeIdFk)} — ASD escalations for this store will no longer reach anyone.`
        : `\n\nWARNING: this is the last active rep for ${storeLabel(rep.storeIdFk)} — handoff emails for this store will stop.`
      : '';
    const confirmed = await confirm({
      title: `Deactivate ${rep.firstName} ${rep.lastName}?`,
      message: `${rep.firstName} ${rep.lastName} (${rep.email}) will be removed from ${storeLabel(rep.storeIdFk)}.${warning}`,
      confirmLabel: 'Deactivate',
    });
    if (!confirmed) return;
    setError('');
    try {
      await api.delete(`/admin/sales-reps/${rep.id}`);
      setReps((prev) => prev.filter((r) => r.id !== rep.id));
      setSuccess(`${rep.firstName} ${rep.lastName} deactivated`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      console.error('Error deactivating sales representative:', err);
      setError(err.response?.data?.message || 'Failed to deactivate sales representative');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRep(null);
    setError('');
    setFormData(emptyForm);
    setFormErrors({});
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
      formErrors[field] ? 'border-red-400' : 'border-gray-300'
    }`;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Users}
        title="Handoff Recipients"
        subtitle="Sales reps receive every handoff email; ASD contacts only an opted-in manual one"
        rightContent={
          <>
            {!loading && (
              <div className="text-right text-sm text-blue-100 mr-1 leading-tight">
                <div>
                  <span className="font-semibold text-white">{summary.covered}</span> of{' '}
                  <span className="font-semibold text-white">{summary.total}</span> stores have reps
                </div>
                <div>
                  <span className="font-semibold text-white">{summary.withAsd}</span> of{' '}
                  <span className="font-semibold text-white">{summary.total}</span> have ASD
                </div>
              </div>
            )}
            <button
              onClick={loadAll}
              title="Refresh"
              className="p-2.5 rounded-xl text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </>
        }
      />

      {success && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-4 shadow-sm flex">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="ml-3 text-sm font-medium text-green-800">{success}</p>
        </div>
      )}
      {error && !openDialog && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm flex">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <AdminLoading message="Loading sales representatives..." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Store overview list */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden self-start">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Stores</h3>
              <p className="text-xs text-gray-500 mt-0.5">Reps and ASD per store</p>
            </div>
            <div className="px-5 py-4 border-b border-gray-200 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Enterprise</label>
                <select
                  value={selectedEnterpriseId}
                  onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : '')}
                  className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Enterprises</option>
                  {enterprises.map((ent) => (
                    <option key={ent.id} value={ent.id}>{ent.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">BOC</label>
                <select
                  value={selectedBocId}
                  onChange={(e) => handleBocChange(e.target.value ? Number(e.target.value) : '')}
                  disabled={!selectedEnterpriseId}
                  className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">All BOCs</option>
                  {bocsForSelectedEnterprise.map((boc) => (
                    <option key={boc.id} value={boc.id}>{boc.name || boc.bocId}</option>
                  ))}
                </select>
              </div>
            </div>
            {filteredStores.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No stores found.</div>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
                {filteredStores.map((store) => {
                  const count = repsFor(store.id).length;
                  const configured = hasConfig(store.id);
                  const isSelected = selectedStoreId === store.id;
                  return (
                    <li key={store.id}>
                      <button
                        onClick={() => setSelectedStoreId(store.id)}
                        className={`w-full text-left px-5 py-3.5 flex items-center justify-between transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{store.name || store.storeId}</p>
                          <p className="text-xs text-gray-500 flex items-center mt-0.5">
                            <span className="font-mono">{store.storeId}</span>
                            <span className="mx-1.5">·</span>
                            <Mail className="h-3 w-3 mr-1" />
                            {configured ? 'SMTP configured' : 'no SMTP config'}
                          </p>
                        </div>
                        <div className="flex items-center flex-shrink-0 ml-3">
                          <div className="flex items-center gap-1.5">
                            {TYPE_LIST.map((t) => {
                              const n = repsFor(store.id, t.key).length;
                              return (
                                <span
                                  key={t.key}
                                  className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                    n > 0 ? t.chip : t.emptyChip
                                  }`}
                                  title={`${n} ${t.title}`}
                                >
                                  {n} {t.countNoun}{n === 1 ? '' : 's'}
                                </span>
                              );
                            })}
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-300 ml-2" />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-3">
            {selectedStoreId == null ? (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center">
                <Users className="h-12 w-12 mb-3 text-gray-300" />
                <p className="text-sm">Select a store on the left to view its sales representatives.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedReps.length > 0 && !hasConfig(selectedStoreId) && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 shadow-sm flex">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div className="ml-3 text-sm text-amber-800">
                      <p className="font-semibold">These recipients won't receive handoff emails yet.</p>
                      <p className="mt-0.5">No SMTP config exists for this store. Add one on the Email Config page.</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {TYPE_LIST.map((t) => {
                    const list = repsFor(selectedStoreId, t.key);
                    return (
                      <div
                        key={t.key}
                        className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex flex-col"
                      >
                        <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 flex items-start justify-between gap-3">
                          <div className="flex items-start min-w-0">
                            <div className={`p-1.5 rounded-lg mr-3 flex-shrink-0 ${t.iconBg}`}>
                              <Users className={`h-4 w-4 ${t.iconColor}`} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-gray-900">
                                {t.title} ({list.length})
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">{t.blurb}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddNew(t.key)}
                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-white flex-shrink-0 transition-colors ${t.button}`}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            {t.addLabel}
                          </button>
                        </div>

                        {list.length === 0 ? (
                          <div className="px-5 py-10 text-center text-gray-500 flex-1">
                            <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm mb-3">No {t.noun}s for this store yet.</p>
                            <button
                              onClick={() => handleAddNew(t.key)}
                              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors ${t.button}`}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {t.addLabel}
                            </button>
                          </div>
                        ) : (
                          <ul className="divide-y divide-gray-200 flex-1">
                            {list.map((rep) => (
                              <li
                                key={rep.id}
                                className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {rep.firstName} {rep.lastName}
                                  </p>
                                  <p className="text-sm text-gray-600 truncate">{rep.email}</p>
                                </div>
                                <AdminRowActions className="flex-shrink-0">
                                  <AdminActionButton variant="edit" onClick={() => handleEdit(rep)} />
                                  <AdminActionButton variant="deactivate" icon={UserX} onClick={() => handleDeactivate(rep)} />
                                </AdminRowActions>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add / Edit dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-xl bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingRep ? dialogMeta.editLabel : dialogMeta.addLabel}
              </h3>
              <button onClick={handleCloseDialog} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            {error && (
              <div role="alert" className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm mb-4 flex">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className={inputClass('firstName')}
                    disabled={submitting}
                  />
                  {formErrors.firstName && <p className="mt-1 text-xs text-red-600">{formErrors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className={inputClass('lastName')}
                    disabled={submitting}
                  />
                  {formErrors.lastName && <p className="mt-1 text-xs text-red-600">{formErrors.lastName}</p>}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass('email')}
                  placeholder="rep@dealership.com"
                  disabled={submitting}
                />
                {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Store</label>
                <select
                  value={formData.storeIdFk}
                  onChange={(e) => setFormData({ ...formData, storeIdFk: e.target.value })}
                  className={`${inputClass('storeIdFk')} bg-white`}
                  disabled={submitting}
                >
                  <option value="">Select a store</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name || store.storeId}
                    </option>
                  ))}
                </select>
                {formErrors.storeIdFk && <p className="mt-1 text-xs text-red-600">{formErrors.storeIdFk}</p>}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingRep ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default SalesRepsManagement;
